import { Injectable } from '@angular/core';
import { AuthSessionService } from './auth-session.service';
import { OidcSessionService } from './oidc-session.service';
import { RedirectionContextSessionService } from './redirection-context-session.service';
import { UnauthenticatedUserSessionService } from './unauthenticated-user-session.service';
import { MockSessionService } from './mock-session.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { SessionService } from '../../modules/administration/services/session.service';
import { AccessDeniedService } from '../../modules/shared/services/access-denied.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { RedirectionContextService } from '../../modules/administration/services/redirection-context.service';
import { HabilitationService } from '../../modules/administration/services/habilitation.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthSessionFactoryService {

  constructor(
    private oauthService: OAuthService,
    private session: SessionService,
    private redirectionContextService: RedirectionContextService,
    private habilitationService: HabilitationService,
    private accessDeniedService: AccessDeniedService
  ) { }

  /**
   * Create an OIDC-based AuthService.
   * 
   * This is the case when the user uses the application from the portal.
   * @returns an OIDC-based AuthService
   */
    public createOidcSession(): AuthSessionService {
        return new OidcSessionService(
          this.oauthService,
          this.session,
          this.habilitationService
        )
    }
  
    /**
     * Create a OAuth AuthService based on the OAuth token provided by
     * a redirection to the app via the redirection context API.
     *
     * @param accessToken the OAuth token provided by
     *        the redirection context API via the redirection URL
     * @param redirectionContextId the id of the redirection context,
     *        provided via the redirection URL
     * @returns an OAuth-based session service
     */
    public createRedirectionContextSession(
      accessToken: string,
      redirectionContextId: string,
      validationToken: string
    ): AuthSessionService {
        return new RedirectionContextSessionService(
          this.session,
          this.redirectionContextService,
          this.accessDeniedService,
          accessToken,
          redirectionContextId,
          validationToken
        )
    }
  
    /**
     * Create a autho session that prevents the user from usingthe application.
     * 
     * This is meant for unauthenticated users when the application cannot
     * provide an authentication method.
     * For example, in environment where the IDP is unavailable
     * (such as the RIE), but the user did not reach the application
     * with a redirection context.
     * 
     * @returns a session service that doe snot allow access to the application
     */
    public createUnauthoziedUserSession(): AuthSessionService {
        return new UnauthenticatedUserSessionService(this.accessDeniedService)
    }
  
    /**
     * Create a mocked session service, for tests in environments where
     * no authentication service is available.
     * 
     * This MUST NOT be used in production.
     * 
     * @returns a mocked session service
     */
    public createMockSession(): AuthSessionService {
        return new MockSessionService(this.session)
    }

    /**
     * Create the correct session type to use based on the environment
     * and the route the user is using,
     * 
     * @param route a snpashot of the route the user is using
     * @returns the appropriate auth session
     */
    public createSession(route: ActivatedRouteSnapshot): AuthSessionService {
      if (environment.authentication_required === 'false') {
        // Authentication is disabled, mock the session
        return this.createMockSession()
      } else if (route.url[0]?.path === "authentifierProfilAcheteur") {
        // Use a redirection context when on a API target URL 
        const accessToken = route.queryParamMap.get('access_token');
        const redirectionContextId = route.queryParamMap.get('redirection_context_id');
        const validationToken = route.queryParamMap.get('validation_token');
  
        return this.createRedirectionContextSession(accessToken, redirectionContextId, validationToken);
      } else if (environment.oidc_enabled === 'true') {
        // If OIDC is enabled, use it
        return this.createOidcSession();
      } else {
        // Otherwise, lock the user out of the application
        return this.createUnauthoziedUserSession();
      }  
    }
}
